import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFragment } from "react-relay";

import { faEllipsisH } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { isPast, isToday, subMinutes } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { noop } from "lodash";
import { graphql } from "relay-runtime";
import useResizeObserver from "use-resize-observer";

import useThrottledValue from "~/lib/hooks/useThrottledValue";
import { inCalendarMode, inVideocallMode, isTeams } from "~/lib/utils";
import { useCompanyFilterFlagOn } from "~/recordings/Filters/Filters";
import MeetingPhaseContext from "~/stream/MeetingPhaseContext";
import Button from "~/ui/button";
import Menu from "~/ui/menu";

import liveReactions from "../LiveReactions";
import askFellow from "./AskFellow";
import autoSendToEveryone, { autoShareIsSetToEveryone } from "./AutoSendToEveryone";
import carryForward, { useCarryItemsForward } from "./CarryForward";
import endMeeting from "./EndMeeting";
import filterByCompany, { useNoteExternalDomains } from "./FilterByCompany";
import giveFeedback from "./GiveFeedback";
import { briefMe, useShowMeetingBriefSuggestedAction } from "./MeetingBriefs";
import { type AbstractAction, OverflowContext } from "./shared";

import type { SuggestedActions_note$key } from "~/__generated__/SuggestedActions_note.graphql";
import type { SuggestedActions_viewer$key } from "~/__generated__/SuggestedActions_viewer.graphql";

import noteCardStyles from "../css/NoteCard.module.css";
import styles from "./SuggestedActions.module.css";

const viewerFragment = graphql`
	fragment SuggestedActions_viewer on Viewer {
		user {
			isGuest
		}
		account {
			allowMeetingRecording
			allowPublicRecap
		}
		...LiveReactions_viewer
	}
`;

const noteFragment = graphql`
	fragment SuggestedActions_note on Note {
		id
		viewerSentMeetingFeedback

		event {
			isOneOnOne
			conferenceCalls {
				provider
				url
			}
			hasUsersWithExternalEmail
		}

		account {
			allowAiFeatures
		}

		meetingRecordings {
			id
			artifactsReady
		}

		...EndMeeting_note
		...CarryForward_note
		...AutoSendToEveryone_note
		...AutoSendToEveryone_autoShareIsSetToEveryone_note
		...AskFellowHooks_useAskFellowButton_note
		...MeetingBriefsSuggestedAction_note
		...MeetingBriefs_useShowMeetingBriefSuggestedAction
		...FilterByCompany_note
	}
`;

const ALL_ACTIONS = [
	endMeeting,
	giveFeedback,
	carryForward,
	autoSendToEveryone,
	liveReactions,
	briefMe,
	filterByCompany,
	askFellow,
] satisfies readonly AbstractAction[];
type ConcreteAction = (typeof ALL_ACTIONS)[number] & { additionalProps?: { [key: string]: string } };
type ActionProps = ConcreteAction["button"] extends (props: infer P) => unknown ? P : never;

interface ActionMethodsContextValue {
	openMeetingFeedbackModal: () => void;
	carryForwardCount: number;
	carryForward: () => void;
}

export const ActionMethodsContext = React.createContext<ActionMethodsContextValue>({
	openMeetingFeedbackModal: noop,
	carryForwardCount: 0,
	carryForward: noop,
});
ActionMethodsContext.displayName = "ActionMethodsContext";

interface SuggestedActionsProps {
	viewer: SuggestedActions_viewer$key;
	// eslint-disable-next-line camelcase
	note: SuggestedActions_note$key;

	// To be passed to "Give feedback" action
	openMeetingFeedbackModal: () => void;

	// For whatever action that needs the socket group name
	noteGroupName: string;

	onHeightChanged: (height: number) => void;
}

export default function SuggestedActions({
	viewer: viewerKey,
	note: noteKey,
	onHeightChanged,
	...propsForActions
}: SuggestedActionsProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const {
		phase: meetingPhase,
		noMeeting,
		event: { start: eventStart },
		copilotStatus: _copilotStatus,
	} = useContext(MeetingPhaseContext);

	const copilotStatus = useThrottledValue(_copilotStatus, 500);

	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);
	const { viewerSentMeetingFeedback: feedbackSent } = note;

	const [overflowingActions, _setOverflowingActions] = useState<Array<string>>([]);
	const addToMenu = useCallback((id: string) => _setOverflowingActions(actions => [...actions, id]), []);
	const removeFromMenu = useCallback(
		(id: string) => _setOverflowingActions(actions => actions.filter(actionName => actionName !== id)),
		[],
	);

	const [meetingStartingSoon, setMeetingStartingSoon] = useState(() => isPast(subMinutes(eventStart, 5)));
	useEffect(() => {
		let timeout: number | undefined;
		if (!meetingStartingSoon && isToday(eventStart)) {
			timeout = window.setTimeout(
				() => setMeetingStartingSoon(true),
				eventStart.getTime() - Date.now() - 5 * 60 * 1000,
			);
		}
		return () => {
			if (timeout) clearTimeout(timeout);
		};
	}, [meetingStartingSoon, eventStart]);

	const autoSendToEveryoneIsOff = !autoShareIsSetToEveryone(note);
	/** If auto-send to everyone either was off when the component mounted, or has been turned off since. */
	const autoSendToEveryoneHasEverBeenOff = useRef(autoSendToEveryoneIsOff);
	if (autoSendToEveryoneIsOff) {
		autoSendToEveryoneHasEverBeenOff.current = true;
	}

	const showAutoSendToEveryone =
		!viewer.user.isGuest &&
		autoSendToEveryoneHasEverBeenOff.current &&
		viewer.account.allowPublicRecap &&
		note.event?.hasUsersWithExternalEmail &&
		["scheduled", "waiting", "starting", "recording"].includes(copilotStatus.type) &&
		meetingPhase !== "after"; // Theoretically this shouldn't ever be "after" given the copilot status, but who knows

	const [, itemsAvailableToCarryForward] = useCarryItemsForward(note);

	const showBriefMe = useShowMeetingBriefSuggestedAction(note);

	const overflowContextValue = useMemo(
		() => ({
			rootRef: containerRef,
			addToMenu,
			removeFromMenu,
		}),
		[addToMenu, removeFromMenu],
	);

	const externalAttendeeDomains = useNoteExternalDomains({ note: note });
	const isCompanyFilterEnabled = useCompanyFilterFlagOn();

	const actions = useMemo(() => {
		const allActions: ConcreteAction[] = [];

		if (!inVideocallMode && !inCalendarMode && !isTeams && !viewer.user.isGuest) {
			allActions.push(askFellow);
		}

		if (
			(inVideocallMode && meetingPhase !== "after") ||
			meetingPhase === "started" ||
			(meetingPhase === "before" && meetingStartingSoon)
		) {
			// This condition is repeated later but we want this to always show first
			allActions.push(liveReactions);
		}

		if (isCompanyFilterEnabled && externalAttendeeDomains.length > 0 && !inVideocallMode) {
			externalAttendeeDomains.forEach(domain =>
				allActions.push({
					...filterByCompany,
					additionalProps: { domain, key: domain },
				}),
			);
		}

		if (showAutoSendToEveryone) {
			allActions.push(autoSendToEveryone);
		}

		if (showBriefMe) {
			allActions.push(briefMe);
		}

		if (meetingPhase === "started") {
			allActions.push(endMeeting);
		}

		if ((meetingPhase !== "after" || noMeeting) && itemsAvailableToCarryForward > 0) {
			allActions.push(carryForward);
		}

		if (meetingPhase !== "before" && !note.event?.isOneOnOne && !feedbackSent && !inVideocallMode) {
			allActions.push(giveFeedback);
		}

		return allActions;
	}, [
		meetingPhase,
		meetingStartingSoon,
		isCompanyFilterEnabled,
		externalAttendeeDomains,
		showAutoSendToEveryone,
		showBriefMe,
		noMeeting,
		itemsAvailableToCarryForward,
		note.event?.isOneOnOne,
		feedbackSent,
		viewer.user.isGuest,
	]);

	const actionsProps: ActionProps = useMemo(
		() => ({ viewer, note, noteId: note.id, ...propsForActions }),
		[viewer, note, propsForActions],
	);

	const { ref: footerRef } = useResizeObserver<HTMLElement>({
		box: "border-box",
		onResize: ({ height }) => {
			if (height !== undefined && onHeightChanged) onHeightChanged(height);
		},
	});

	return (
		<div ref={footerRef} className={styles.footerContainer}>
			<footer
				className={cx(styles.footer, noteCardStyles.footer, {
					[styles.isVisible]: actions.length > 0,
				})}
			>
				{actions.length > 0 && (
					<OverflowContext.Provider value={overflowContextValue}>
						<hr />
						<div ref={containerRef} className={styles.suggestedActions}>
							<AnimatePresence>
								{actions.map(({ id, button: Button, additionalProps }) => (
									<motion.div key={additionalProps?.key ?? id} layout>
										<Button {...actionsProps} {...additionalProps} />
									</motion.div>
								))}
							</AnimatePresence>
							{overflowingActions.length > 0 && (
								<Menu>
									<Menu.Trigger>
										<Button size="slim" variant="tertiary" className={styles.moreButton}>
											<FontAwesomeIcon icon={faEllipsisH} />
											&nbsp;More
										</Button>
									</Menu.Trigger>
									<Menu.Content align="end">
										{actions
											.filter(({ id }) => overflowingActions.includes(id))
											.map(({ id, menuItem: MenuItem, additionalProps }) => (
												<MenuItem key={additionalProps?.key ?? id} {...actionsProps} {...additionalProps} />
											))}
									</Menu.Content>
								</Menu>
							)}
						</div>
					</OverflowContext.Provider>
				)}
			</footer>
		</div>
	);
}
